/**
 * Address Update Announcement Bar
 * Premium, non-intrusive sticky bar that reminds users to update their address
 * Shows personalized message with smart timing and dismissal logic
 */

import React, { useEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';
import { X, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAddresses } from '../hooks/useAddresses';
import { useSmartReminder } from '../hooks/useSmartReminder';
import { shouldShowReminder, dismissReminder } from '../services/dismissalStorage';
import { useNavigate } from 'react-router-dom';

interface AddressUpdateAnnouncementProps {
  position?: 'top' | 'bottom';
  delay?: number; // milliseconds before showing (default: 5000)
  scrollThreshold?: number; // percentage 0-100 (default: 30)
  onDismiss?: () => void;
}

export const AddressUpdateAnnouncement: React.FC<AddressUpdateAnnouncementProps> = ({
  position = 'top',
  delay = 5000,
  scrollThreshold = 30,
  onDismiss,
}) => {
  const { currentUser } = useAuth();
  const { addresses } = useAddresses(currentUser?.uid);
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const announcementRef = useRef<HTMLDivElement>(null);

  // Get selected/default address
  const selectedAddress = addresses?.find(addr => addr.SelectedAddress) || addresses?.[0];

  // Check if reminder should be shown
  const shouldShow = currentUser?.uid && selectedAddress && shouldShowReminder(currentUser.uid, 'announcement');

  // Smart timing hook
  const { shouldShow: smartShow, hide } = useSmartReminder({
    delay,
    scrollThreshold,
    enabled: Boolean(shouldShow && selectedAddress),
  });

  useEffect(() => {
    if (smartShow && shouldShow) {
      setIsVisible(true);
    }
  }, [smartShow, shouldShow]);

  // Animate announcement
  useEffect(() => {
    if (isVisible && announcementRef.current) {
      gsap.fromTo(
        announcementRef.current,
        { opacity: 0, y: position === 'top' ? -20 : 20 },
        { opacity: 1, y: 0, duration: 0.3, ease: "power2.out" }
      );
    } else if (!isVisible && announcementRef.current) {
      gsap.to(announcementRef.current, {
        opacity: 0,
        y: position === 'top' ? -20 : 20,
        duration: 0.3,
        ease: "power2.in"
      });
    }
  }, [isVisible, position]);

  const handleDismiss = () => {
    if (currentUser?.uid) {
      dismissReminder(currentUser.uid, 'announcement');
    }
    setIsVisible(false);
    hide();
    onDismiss?.();
  };

  const handleUpdateAddress = () => {
    navigate('/settings/addresses');
    handleDismiss();
  };

  // Don't show if no user, no address, or dismissed
  if (!shouldShow || !selectedAddress || !isVisible) {
    return null;
  }

  const cityName = selectedAddress.City || 'your location';

  // Header height: h-14 (56px) on mobile, h-16 (64px) on desktop
  // Position announcement bar below header
  const containerClasses = position === 'top' 
    ? 'fixed left-0 right-0 z-40 top-14 md:top-16'
    : 'fixed bottom-0 left-0 right-0 z-40';

  return (
    <div
      ref={announcementRef}
      className={containerClasses}
    >
          <div className="bg-slate-50 border-b border-slate-200 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-12 sm:h-14">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <MapPin className="w-4 h-4 text-primary-600 flex-shrink-0" />
                  <p className="text-sm text-slate-700 flex-1 min-w-0">
                    <span className="hidden sm:inline">
                      🎉 <strong>Great news!</strong> We've expanded our delivery coverage with updated address regions. Is <span className="font-medium">{cityName}</span> still your home? Help us deliver faster and more accurately by confirming your precise address details—street, ward, and exact location.{' '}
                    </span>
                    <span className="sm:hidden">
                      🎉 New regions! Update your address details?{' '}
                    </span>
                    <button
                      onClick={handleUpdateAddress}
                      className="text-primary-600 hover:text-primary-700 underline font-medium transition-colors ml-1"
                    >
                      Update Address
                    </button>
                  </p>
                </div>
                <button
                  onClick={handleDismiss}
                  className="ml-4 flex-shrink-0 p-1 text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label="Dismiss announcement"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
  );
};

export default AddressUpdateAnnouncement;

